import '../../style/caet/bindEmail.scss';
import { lang } from '@umu/ukit-lang';
import { Button, Iconfont, Modal, UmuInput, SnackBar } from '@umu/umu-design';
import React, { forwardRef } from 'react';
import { setCertificateSetting, receiveCertificate } from './api/server';
import { RecognizeObjectType } from './interface/api';
import { useCaetBindEmail } from './hook/useCaetBindEmail';
import { getInputCountType, getChartLimitLenByLang } from '../../../../@utils/inputStatistics';
import type {
    ICaetBindEmailDialogProps,
    ICaetBindEmailProps,
    ICaetBindEmailRef,
    IFormData,
} from './interface/bindEmail';
import { MAX_USER_NAME_LENGTH } from './dataFlow';
const CaetBindEmail = forwardRef<ICaetBindEmailRef, ICaetBindEmailProps>((props, ref) => {
    // 使用自定义 Hook 管理逻辑
    const {
        formData,
        errors,
        showFooterButton,
        showTooltips,
        showHeaderButton,
        preClassName,
        handleInputChange,
        handleConfirm,
        handleAfterReceive,
    } = useCaetBindEmail({
        ...props,
        ref,
    });

    return (
        <div className={`bind-email-ai-power-recognition ${preClassName}`}>
            {showHeaderButton && (
                <div className="bind-email-ai-power-recognition-header">
                    <Iconfont type="SmallBack" className="back-icon" onClick={props.onClose} />
                    {props?.title || ''}
                    <Button type="primary" className="close-icon" onClick={handleConfirm}>
                        {lang('确认')}
                    </Button>
                </div>
            )}
            {/* 提示信息区域 */}
            {showTooltips && (
                <div className="info-section">
                    <Iconfont type="ExclamationCircleFilled" className="info-icon" />
                    <p className="info-text">
                        {lang(
                            '首次领取需填写邮箱与证书显示姓名。提交后,系统会把证书发送至该邮箱。请填写长久有效的邮箱地址,用于接收或找回证书。',
                        )}
                    </p>
                </div>
            )}

            {/* 表单区域 */}
            <div className="form-section">
                {/* 证书展示姓名 */}
                <div className="form-item">
                    <label className="form-label">
                        {lang('证书展示姓名')}
                        <span className="required">*</span>
                    </label>
                    <UmuInput
                        value={formData.certificateName}
                        countType={getInputCountType()}
                        maxLength={getChartLimitLenByLang(MAX_USER_NAME_LENGTH)}
                        onChange={({ value }) => handleInputChange('certificateName', value)}
                        error={errors.certificateName}
                        expandConfig={{ placeholder: lang('展示在证书上的姓名') }}
                    />
                </div>

                {/* 邮箱 */}
                <div className="form-item">
                    <label className="form-label">
                        {lang('邮箱')}
                        <span className="required">*</span>
                    </label>
                    <UmuInput
                        value={formData.email}
                        onChange={({ value }) => handleInputChange('email', value)}
                        error={errors.email}
                        expandConfig={{ placeholder: lang('邮箱地址') }}
                    />
                </div>

                {/* 确认邮箱 */}
                <div className="form-item">
                    <label className="form-label">
                        {lang('确认邮箱')}
                        <span className="required">*</span>
                    </label>
                    <UmuInput
                        value={formData.confirmEmail}
                        onChange={({ value }) => handleInputChange('confirmEmail', value)}
                        error={errors.confirmEmail}
                        expandConfig={{ placeholder: lang('确认邮箱地址') }}
                    />
                </div>
            </div>

            {/* 自定义按钮区域 */}
            {showFooterButton && (
                <div className="form-footer">
                    <Button type="ghost-grey" onClick={handleAfterReceive}>
                        {lang('稍后领取')}
                    </Button>
                    <Button type="primary" onClick={handleConfirm}>
                        {lang('确认领取')}
                    </Button>
                </div>
            )}
        </div>
    );
});

const { confirm } = Modal;
const defaultSetCertificateSetting = async (
    info: IFormData,
    config: {
        id: string;
        type: RecognizeObjectType;
    },
) => {
    const { isSuccess, message } = await setCertificateSetting({
        user_name: info.certificateName,
        email: info.email,
    });
    if (isSuccess) {
        const { isSuccess: isSuccessReceive, message: messageReceive } = await receiveCertificate({
            obj_id: config.id,
            obj_type: config.type,
        });
        return {
            isSuccess: isSuccessReceive,
            message: messageReceive,
        };
    }
    return {
        isSuccess,
        message,
    };
};
const onDialogCaetBindEmail = (data?: ICaetBindEmailDialogProps) => {
    const {
        showBack = true,
        id = '',
        type = RecognizeObjectType.COURSE,
        title = lang('领取所需信息'),
        onBack,
        onSuccess,
        afterClose,
        onAfterReceive,
        onConfirm = (info: IFormData) => defaultSetCertificateSetting(info, { id, type }),
    } = data || {};
    const handleClose = () => {
        dialog.destroy();
    };
    const handleBack = (): void => {
        onBack?.();
        handleClose();
    };
    const handleConfirm = async (info: IFormData) => {
        const { isSuccess, message } = await onConfirm?.(info);
        if (isSuccess) {
            onSuccess?.(info);
            handleClose();
        } else {
            SnackBar.show({
                message: message || lang('绑定邮箱失败'),
            });
        }
    };
    // 创建一个 ref 来访问组件的方法
    let componentRef: {
        getValueInfo: () => IFormData;
        validateForm: () => boolean;
    } | null = null;

    const dialog = confirm({
        title: showBack ? (
            <div className="bind-email-ai-power-recognition-title">
                <Iconfont type="SmallBack" className="back-icon" onClick={handleBack} />
                {title}
            </div>
        ) : (
            title
        ),
        centered: true,
        closable: true,
        width: '4.5rem',
        footer: null, // 禁用默认按钮，自己控制
        afterClose,
        content: (
            <CaetBindEmail
                onClose={handleClose}
                onAfterReceive={onAfterReceive}
                onConfirm={() => {
                    // 获取表单数据并处理
                    if (componentRef?.getValueInfo) {
                        const formData = componentRef.getValueInfo();
                        handleConfirm(formData);
                    }
                }}
                ref={(ref) => {
                    componentRef = ref;
                }}
            />
        ),
    });
    return {
        dialog,
        handleClose,
    };
};
export { CaetBindEmail, defaultSetCertificateSetting, onDialogCaetBindEmail };
